<template>
    <div class="stats-box h-100">
        <span class="custom-tooltip">
            <Translate :translationKey="translationKey"
                       :fallback="props.translationFallback"
                       :replacements="{currency_code: currencyCode}" />
            <i ref="infoIconRef"
               class="fa-solid fa-circle-info tippy-info hide-mobile-landscape pl-1"
               aria-label="Info tooltip" />
        </span>
        <div class="d-flex flex-column flex-lg-row">
            <div class="col-md-7 px-0">
                <h4 class="pt-0 pb-0 mb-0 font-weight-normal">
                    {{ formattedCurrency ? formattedCurrency : price }}
                    <span v-if="dappProtocol && formattedPrice"
                          class="balance">
                        {{ formattedPrice }}
                        {{ dappProtocol }}
                    </span>
                    <span v-if="showDifference">
                        <span v-if="difference !== 0"
                              :class="typeof difference === 'number' && difference > 0 ? 'percentage-change-positive' : 'percentage-change-negative'">
                            <i :class="typeof difference === 'number' && difference > 0 ? 'fa-solid fa-caret-up' : 'fa-solid fa-caret-down'" /> {{
                                difference === 0 ? difference : typeof difference === "number" && difference.toFixed(2)
                            }}%
                        </span>
                    </span>
                </h4>
            </div>
            <div class="col-md-5 px-1 pt-lg-0 pt-3">
                <Trend :data="dappActivity"
                       :gradient="['#9884fc']"
                       :padding="0"
                       :radius="2"
                       :stroke-width="4"
                       auto-draw
                       :height="40"
                       smooth />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, ref, h } from 'vue';
    import Trend from 'vue3trend';
    import useTippyPopup from '../composables/useTippyPopup';
    import { day, week, month } from '../constants/timeframe';
    import { getTrans } from '../helpers/getTrans';
    import DappStatsCardTooltipContent from './DappStatsCardTooltipContent.vue';

    const infoIconRef = ref();

    interface Props {
        translationKey: string;
        translationFallback: string;
        tooltipTranslationKey: string;
        tooltipTranslationFallback: string;
        tooltipLearnMoreTextKey?: string;
        tooltipLearnMoreTextFallback?: string;
        tooltipLearnMoreLink?: string;
        currencyCode?: string;
        formattedCurrency?: string;
        selectedTimeFrame?: string;
        formattedPrice?: string | number;
        dappActivity?: number[];
        dappProtocol?: string;
        showDifference?: boolean;
        tippyTemplate?: string;
        dayChange?: number | string;
        weekChange?: number | string;
        monthChange?: number | string;
        dayPrice?: number | string;
        weekPrice?: number | string;
        monthPrice?: number | string;
    }
    const props = withDefaults(defineProps<Props>(), {
        tooltipLearnMoreTextKey: '',
        tooltipLearnMoreTextFallback: '',
        tooltipLearnMoreLink: '',
        currencyCode: '',
        formattedCurrency: '',
        selectedTimeFrame: '',
        formattedPrice: '',
        dappActivity: () => [],
        dappProtocol: '',
        showDifference: false,
        tippyTemplate: '',
        dayChange: 0,
        weekChange: 0,
        monthChange: 0,
        dayPrice: '',
        weekPrice: '',
        monthPrice: '',
    });

    const difference = computed(() => {
        switch (props.selectedTimeFrame) {
        case day:
            return props.dayChange;
        case week:
            return props.weekChange;
        case month:
            return props.monthChange ? props.monthChange : 0;
        default:
            return 0;
        }
    });

    const price = computed(() => {
        switch (props.selectedTimeFrame) {
        case day:
            return props.dayPrice;
        case week:
            return props.weekPrice;
        case month:
            return props.monthPrice;
        default:
            return '';
        }
    });

    useTippyPopup(infoIconRef, {
        interactive: true,
        content: h(DappStatsCardTooltipContent, {
            text: getTrans(props.tooltipTranslationKey, props.tooltipTranslationFallback, { currency_code: props.currencyCode }),
            link: props.tooltipLearnMoreLink,
            linkText: getTrans(props.tooltipLearnMoreTextKey, props.tooltipLearnMoreTextFallback),
        }),
    });
</script>
